import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import {
  registeredUserCompletePageQueryOptions,
  type RegisteredUserCompleteSearch,
} from "~/app/api/pageData/registeredUserCompletePageQuery";
import type {
  CompleteRegistrationAddressData,
  CompleteRegistrationCustomerData,
  CompleteRegistrationPageInitialData,
  CompleteRegistrationSalesPartnerData,
  CompleteRegistrationSubsidiaryData,
} from "~/app/api/pageData/registeredUserCompletePageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  createCompleteRegistrationContainer,
  type CreateProps,
} from "~/pages/CompleteRegistrationPage/CompleteRegistrationPage";

const REGISTERED_USER_COMPLETE_PATH = "/registered/user/complete";

const orEmpty = (value: string | null | undefined): string => value ?? "";

interface CodeNameOption {
  code: string;
  name: string;
}

interface IsocodeNameOption {
  isocode: string;
  name: string;
}

function toTitleOptions(items: { code: string; name?: string }[]): CodeNameOption[] {
  return items.map((item) => ({ code: item.code, name: orEmpty(item.name) }));
}

function toNamedCodeOptions(items: { code: string; name: string }[]): CodeNameOption[] {
  return items.map((item) => ({ code: item.code, name: item.name }));
}

function toCountryOptions(countries: { isocode: string; name?: string }[]): IsocodeNameOption[] {
  return countries.map((country) => ({ isocode: country.isocode, name: orEmpty(country.name) }));
}

function toMapEntries(entries: Record<string, string>): CodeNameOption[] {
  return Object.entries(entries).map(([key, value]) => ({ code: key, name: orEmpty(value) }));
}

function toLanguageOptions(languages: { isocode: string; name?: string }[]): CodeNameOption[] {
  return languages.map((language) => ({ code: language.isocode, name: orEmpty(language.name) }));
}

interface PrefilledCustomerData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  salutationCode: string;
  line1: string;
  line2: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
}

/**
 * Reproduces the legacy {@code GuestCheckoutDataTransformer.transform(CustomerData)} mapping.
 * Nullable transformer outputs are omitted to match the previous Jackson {@code NON_NULL}
 * serialisation; the island treats absent pre-fill fields as empty.
 */
function toPrefilledCustomer(customer: CompleteRegistrationCustomerData): Partial<PrefilledCustomerData> {
  const result: Partial<PrefilledCustomerData> = {};
  if (customer.firstName != null) {
    result.firstName = customer.firstName;
  }
  if (customer.lastName != null) {
    result.lastName = customer.lastName;
  }
  if (customer.email != null) {
    result.email = customer.email;
  }
  if (customer.salutationCode != null) {
    result.salutationCode = customer.salutationCode;
  }
  if (customer.contactNumber != null) {
    result.phone = customer.contactNumber;
  }
  const address = customer.defaultShippingAddress;
  if (address) {
    if (address.companyName != null) {
      result.companyName = address.companyName;
    }
    if (address.country?.isocode != null) {
      result.country = address.country.isocode;
    }
    if (address.postalCode != null) {
      result.zipCode = address.postalCode;
    }
    if (address.streetName != null) {
      result.line1 = address.streetName;
    }
    if (address.additionalLine1 != null) {
      result.line2 = address.additionalLine1;
    }
    if (address.town != null) {
      result.city = address.town;
    }
  }
  return result;
}

/**
 * Reproduces the legacy {@code CompleteRegistrationPageJsonBuilder.buildAddress} mapping:
 * {@code AddressData.townName} → {@code town}, nullable fields omitted (NON_NULL parity).
 *
 * Country-name translation: the legacy builder resolved the country name server-side via
 * {@code schaefflerTranslationUtils.getTranslation(address.country.name)} (core resource
 * bundle lookup with key fallback). The page-data body carries only the raw country name
 * (the translation key), not the resolved locale string. The frontend has no access to the
 * backend core message bundle, so this adapter passes the raw key/name through unchanged.
 * In the common no-bundle-entry case this is byte-identical to the builder fallback; the
 * locale-translated case (e.g. "Germany" → "Deutschland") is a known behavioural gap
 * flagged in the packet report.
 */
function toSubsidiaryAddress(address: CompleteRegistrationAddressData): Partial<SalesPartnerSubsidiaryAddress> {
  const result: Partial<SalesPartnerSubsidiaryAddress> = {};
  if (address.id != null) {
    result.id = address.id;
  }
  if (address.line1 != null) {
    result.line1 = address.line1;
  }
  if (address.line2 != null) {
    result.line2 = address.line2;
  }
  if (address.townName != null) {
    result.town = address.townName;
  }
  if (address.postalCode != null) {
    result.postalCode = address.postalCode;
  }
  if (address.phone != null) {
    result.phone = address.phone;
  }
  if (address.country?.name != null) {
    result.country = address.country.name;
  }
  return result;
}

interface SalesPartnerSubsidiaryAddress {
  id: string;
  line1: string;
  line2: string;
  town: string;
  postalCode: string;
  phone: string;
  country: string;
}

interface SalesPartnerSubsidiary {
  code: string;
  name: string;
  address: Partial<SalesPartnerSubsidiaryAddress>;
}

/**
 * Reproduces the legacy {@code buildSubsidiary} mapping: {@code SubsidiaryDTO.id} →
 * {@code code} and the nested address remap. Nullable fields omitted (NON_NULL parity).
 */
function toSubsidiary(subsidiary: CompleteRegistrationSubsidiaryData): SalesPartnerSubsidiary {
  const result: SalesPartnerSubsidiary = {} as SalesPartnerSubsidiary;
  if (subsidiary.id != null) {
    result.code = subsidiary.id;
  }
  if (subsidiary.name != null) {
    result.name = subsidiary.name;
  }
  if (subsidiary.address) {
    result.address = toSubsidiaryAddress(subsidiary.address);
  }
  return result;
}

/**
 * Reproduces the legacy {@code buildSalesPartner} mapping. Nullable top-level fields and
 * the {@code subsidiaries} collection are omitted when absent (NON_NULL parity).
 */
function toSalesPartner(salesPartner: CompleteRegistrationSalesPartnerData): Partial<SalesPartnerDTO> {
  const result: Partial<SalesPartnerDTO> = {};
  if (salesPartner.affiliateId != null) {
    result.affiliateId = salesPartner.affiliateId;
  }
  if (salesPartner.logo != null) {
    result.logo = salesPartner.logo;
  }
  if (salesPartner.name != null) {
    result.name = salesPartner.name;
  }
  if (salesPartner.subsidiaries) {
    // Global SubsidiaryDTO/AddressDTO types declare all fields required, but the legacy
    // builder omitted nullables (NON_NULL). Downstream transformSalesPartnerDTO reads via
    // optional chaining, so the partial runtime shape is the faithful contract.
    result.subsidiaries = salesPartner.subsidiaries.map(toSubsidiary) as unknown as SubsidiaryDTO[];
  }
  return result;
}

/**
 * Reproduces the legacy {@code CompleteRegistrationPageJsonBuilder.buildReactPageProps}
 * field-level transforms, assembling the {@code CreateProps} shape the
 * {@code createCompleteRegistrationContainer} factory (and its {@code transformSalesPartnerDTO}
 * downstream) consumes.
 *
 * Primitive defaults mirror the previous JSP quoted-EL output: {@code ""} for null
 * email/token, {@code false} for an absent newsletterOne when email is null.
 */
function toCompleteRegistrationProps(data: CompleteRegistrationPageInitialData | undefined): CreateProps {
  if (!data) {
    throw new Error("Complete-registration page-data response is missing its required body");
  }
  const email = orEmpty(data.email);
  return {
    titles: toTitleOptions(data.titleData),
    salutations: toTitleOptions(data.salutationData),
    industrySectors: toMapEntries(data.industrySectorData),
    jobTitles: toMapEntries(data.jobTitleData),
    functionalAreas: toMapEntries(data.functionalAreaData),
    countries: toCountryOptions(data.countryData),
    customerCategories: toNamedCodeOptions(data.customerCategoryData),
    knowledgeAreas: toNamedCodeOptions(data.knowledgeAreaData),
    languages: toLanguageOptions(data.languagesData),
    email,
    passwordToken: orEmpty(data.token),
    validLink: data.validLink,
    newsletterOne: data.email != null && data.newsletterOne,
    // The legacy builder omitted nullable fields via Jackson NON_NULL, so the runtime
    // pre-fill / sales-partner shapes are partial. CreateProps types them as required
    // (pre-existing looseness); the island tolerates absent fields. Casts document the
    // JSON-contract reality without weakening the adapter's faithful partial output.
    ...(data.customer ? { customer: toPrefilledCustomer(data.customer) as NonNullable<CreateProps["customer"]> } : {}),
    ...(data.salesPartner ? { salesPartner: toSalesPartner(data.salesPartner) as SalesPartnerDTO } : {}),
  };
}

function RegisteredUserCompleteRoute() {
  const result = useLoaderData({
    from: REGISTERED_USER_COMPLETE_PATH,
  }) as PageInitialData<CompleteRegistrationPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createCompleteRegistrationContainer(toCompleteRegistrationProps(result.data))}
    </StorefrontShellFrame>
  );
}

export async function loadRegisteredUserCompletePageData(
  context: { queryClient: QueryClient },
  search: RegisteredUserCompleteSearch,
) {
  const result = await context.queryClient.ensureQueryData(registeredUserCompletePageQueryOptions(search));

  return unwrapPageInitialData(result);
}

export const registeredUserCompleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: REGISTERED_USER_COMPLETE_PATH,
  validateSearch: (search: Record<string, unknown>): RegisteredUserCompleteSearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
    affiliateId: typeof search.affiliateId === "string" ? search.affiliateId : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => loadRegisteredUserCompletePageData(context, deps),
  component: RegisteredUserCompleteRoute,
});
